import React, { memo, useCallback, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  ApprovedSP,
  BestFitRecommendation,
  BoardMembership,
  CareerAspiration,
  CareerJourney,
  ContractDetails,
  EDGEView,
  Experiences,
  MobilityPlan,
  OtherContribution,
  Qualification,
  SpokenLanguage,
} from '..';
import OverAllProfileUser from '../../pages/TalentProfilePage/components/OverAllProfileUser/OverAllProfileUser';
import AssessmentPrint from './components/AssessmentPrint/AssessmentPrint';
import DevelopmentPlanPrint from './components/DevelopmentPlanPrint/DevelopmentPlanPrint';
import HistoricalTalentStatusPrint from './components/HistoricalTalentStatusPrint/HistoricalTalentStatusPrint';
import OtherExperiencePrint from './components/OtherExperiencePrint/OtherExperiencePrint';
import PersonalityPrint from './components/PersonalityPrint/PersonalityPrint';
import styles from './talent-profile-download-preview.module.scss';
import TalentProfilePrintSelection from './components/TalentProfilePrintSelection/TalentProfilePrintSelection';
import { TP_PRINT_SECTION_NAME } from './constants';
import WorkPreference from '../WorkPreference/WorkPreference';
import NotesPrint from './components/NotesPrint/NotesPrint';
import TalentReviewPrint from './components/TalentReviewPrint/TalentReviewPrint';
import KeyStrengthDevelopmentAreaPrint from './components/KeyStrengthDevelopmentAreaPrint/KeyStrengthDevelopmentAreaPrint';
import ExternalAssessments from '../../pages/TalentProfilePage/components/ExternalAssessments/ExternalAssessments';
import ExternalExperience from '../ExternalExperience/ExternalExperience';
import TechnicalCompetencies from '../TechnicalCompetencies/TechnicalCompetencies';
import AreaOfInterest from '../AreaOfInterest/AreaOfInterest';
import CareerPreference from '../CareerPreference/CareerPreference';
import AreaOfExpertise from '../AreaOfExpertise/AreaOfExpertise';
import { BasicButton } from '../../assets/common';
import talentProfileApi, { getNotes, getTalentReview } from '../../services/talentProfiles';
import { updateDownloadData } from '../../pages/TalentProfilePage/store/downloadProfileSlice';
import { size } from 'lodash';
import { useDispatch } from 'react-redux';

const TalentProfileDownloadPreview = (props) => {
  const { tokenTalentProfile } = props;
  const { id } = props.roleUser;
  const { selectedValues } = useSelector((state) => state.app.talentProfile.downloadProfile.downloadSettings);
  const printRef = useRef();
  const isRefReady = useCallback((node) => {
    if (node !== null) {
      return node;
    }
  }, []);
  const { fetchedData } = useSelector((state) => state.app.talentProfile.downloadProfile);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!printRef.current) return;

    const updatePrintSections = () => {
      const printSectionsDOM = printRef.current.querySelectorAll('[data-print-section]');
      printSectionsDOM.forEach((element) => {
        const printSection = element.getAttribute('data-print-section');
        if (!selectedValues.includes(printSection)) {
          element.style.display = 'none';
        } else {
          element.style.removeProperty('display');
        }
      });
    };

    updatePrintSections();

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          updatePrintSections();
        }
      }
    });

    observer.observe(printRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [selectedValues]);

  useEffect(() => {
    const fetchFunction = async () => {
      try {
        let resKeyStrengthPills = await talentProfileApi.getTalentProfileKeyStrengthPillsByStaffId({
          userId: id,
          roleUser: props.roleUser,
          tokenTalentProfile,
        });
        let resDevelopmentAreasPills = await talentProfileApi.getTalentProfileDevelopmentAreasPillsByStaffId({
          userId: id,
          roleUser: props.roleUser,
          tokenTalentProfile,
        });
        let resKeyStrengthList = await talentProfileApi.getTalentProfileKeyStrengthListByStaffId({
          userId: id,
          roleUser: props.roleUser,
          tokenTalentProfile,
        });
        let resDevelopmentAreasList = await talentProfileApi.getTalentProfileDevelopmentAreasListByStaffId({
          userId: id,
          roleUser: props.roleUser,
          tokenTalentProfile,
        });

        if (
          size(resKeyStrengthPills.data.result) > 0 ||
          size(resDevelopmentAreasPills.data.result) > 0 ||
          size(resKeyStrengthList.data.result) > 0 ||
          size(resDevelopmentAreasList.data.result) > 0
        ) {
          dispatch(
            updateDownloadData({
              sectionName: TP_PRINT_SECTION_NAME.KEY_STRENGTHS,
              data: {
                keyStrengthPills: resKeyStrengthPills.data.result,
                developmentAreasPills: resDevelopmentAreasPills.data.result,
                keyStrengthList: resKeyStrengthList.data.result,
                developmentAreasList: resDevelopmentAreasList.data.result,
              },
            }),
          );
        }

        const pagination = { first: 0, count: 1000 };
        const res = await getNotes({
          staffId: id,
          roleUser: props?.roleUser,
          params: { ...pagination, is_view_other: false, is_toggle: true },
          tokenTalentProfile,
        });
        if (res.status === 200) {
          const result = res?.data?.result;
          const data = { totalNotes: result?.totalNotes, data: result?.notes };
          dispatch(updateDownloadData({ sectionName: TP_PRINT_SECTION_NAME.NOTES, data }));
        }

        const res1 = await getTalentReview({ roleUser: props?.roleUser, id, tokenTalentProfile });
        if (res1.status === 200) {
          dispatch(updateDownloadData({ sectionName: TP_PRINT_SECTION_NAME.TALENT_REVIEW, data: res1.data.result }));
        }
      } catch (error) {
        console.error('Error fetching Key Strengths and Development Areas details:', error);
      }
    };
    fetchFunction();
  }, [id]);

  return (
    <Modal
      destroy
      onHide={() => props?.closeModal()}
      dialogClassName={styles.containerModal}
      show={props?.isPrinting}
      style={{ overflowX: 'auto' }}
      centered
    >
      <Modal.Body>
        <div className="relative">
          <span className={styles.settingIcon}>
            <TalentProfilePrintSelection />
          </span>
        </div>
        <div ref={printRef} className={styles.printContainer}>
          <div className={styles.distance}>
            <OverAllProfileUser isPrintPdf={true} sid={id} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <CareerJourney
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.CAREER_JOURNEY]}
              id={id}
              roleUser={props?.roleUser}
              setCareerJourneyData={() => {}}
              noDetail={false}
              customStyle={{}}
              tokenTalentProfile={tokenTalentProfile}
              isDownloadPreview={true}
            />
          </div>
          <div className={styles.distance}>
            <CareerAspiration
              isPrinting
              carreerAspiration={props?.carreerAspiration}
              tokenTalentProfile={tokenTalentProfile}
              getTalentProfileSummary={props?.getTalentProfileSummary}
            />
          </div>
          <div className={styles.distance}>
            <KeyStrengthDevelopmentAreaPrint fetchedData={fetchedData[TP_PRINT_SECTION_NAME.KEY_STRENGTHS]} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <PersonalityPrint personality={props?.personality} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <BoardMembership langAndBoard={props?.langAndBoard} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <Qualification qualification={props?.qualification} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <ApprovedSP isPrintTP id={id} roleUser={props?.roleUser} tokenTalentProfile={tokenTalentProfile} isDownloadPreview={true} />
          </div>
          <div className={styles.distance}>
            <SpokenLanguage langAndBoard={props?.lang} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <AreaOfExpertise areaOfExpertise={props?.areaOfExpertise} loading={props?.loadingSummary} tokenTalentProfile={tokenTalentProfile} />
          </div>

          <div className={styles.distance}>
            <EDGEView isPrintTP id={id} roleUser={props?.roleUser} tokenTalentProfile={tokenTalentProfile} isDownloadPreview={true} />
          </div>
          <div className={styles.distance}>
            <TechnicalCompetencies
              isPrintTP
              profileId={id}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.TECHNICAL_COMPETENCY]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>

          <div className={styles.distance}>
            <Experiences
              profileId={id}
              roleUser={props?.roleUser}
              isPrinting={true}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.EXPERIENCE]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <ExternalExperience
              staffId={id}
              role={props?.roleUser}
              isPrinting={true}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.EXTERNAL_EXPERIENCE]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <OtherExperiencePrint
              profileId={id}
              roleUser={props?.roleUser}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.OTHER_EXPERIENCE]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <OtherContribution
              profileId={id}
              isPrintPdf={true}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.OTHER_CONTRIBUTION]}
              fetchedDataProjectInvolvement={fetchedData[TP_PRINT_SECTION_NAME.PROJECT_INVOLVEMENT]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <ExternalAssessments isPrintTP staffId={id} tokenTalentProfile={tokenTalentProfile} isDownloadPreview={true} />
          </div>
          <div className={styles.distance}>
            <DevelopmentPlanPrint
              profileId={id}
              roleUser={props?.roleUser}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.DEVELOPMENT_PLAN]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <MobilityPlan profileId={id} roleUser={props?.roleUser} isPrintPdf={true} />
          </div>
          <div className={styles.distance}>
            <ContractDetails
              profileId={id}
              roleUser={props?.roleUser}
              isPrintPdf={true}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.CONTRACT_DETAIL]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <AssessmentPrint
              profileId={id}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.ASSESSMENT_DATA]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={`${styles.container}  mt-3`}>
            <HistoricalTalentStatusPrint
              profileId={id}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.HISTORICAL_TALENT_STATUS]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <WorkPreference
              isPrintTP
              profileId={id}
              roleUser={props?.roleUser}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.WORK_PREFERENCE]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <AreaOfInterest
              isPrintTP
              profileId={id}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.AREAS_OF_INTEREST]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <CareerPreference
              isPrintTP
              profileId={id}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.CAREER_PREFERENCES]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <BestFitRecommendation
              isPrintTP
              profileId={id}
              roleUser={props?.roleUser}
              fetchedData={fetchedData[TP_PRINT_SECTION_NAME.RIGHT_FIT_RECOMMENDATION]}
              tokenTalentProfile={tokenTalentProfile}
            />
          </div>
          <div className={styles.distance}>
            <NotesPrint fetchedData={fetchedData[TP_PRINT_SECTION_NAME.NOTES]} tokenTalentProfile={tokenTalentProfile} />
          </div>
          <div className={styles.distance}>
            <TalentReviewPrint fetchedData={fetchedData[TP_PRINT_SECTION_NAME.TALENT_REVIEW]} tokenTalentProfile={tokenTalentProfile} />
          </div>
        </div>
        <div className={styles.btn_wrapper} ref={isRefReady}>
          <BasicButton mode="teal" className={styles.downloadBtn} onClick={props?.handleDownload} disabled={props?.downloading}>
            Download ({props?.countOfDownloads}){' '}
            {props?.downloading && <span className="spinner-border" style={{ width: 18, height: 18, marginLeft: 8 }} />}
          </BasicButton>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default memo(TalentProfileDownloadPreview);
